import { useQuery } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import classNames from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { STATUSPAGE_PATH } from '@atlassian/cssxp-typescommon';
import {
  FetchProductsForInvoiceGroupQuery,
  FetchProductsForInvoiceGroupQueryVariables,
  InvoiceGroupProduct,
} from '@css-xp/graphtypes';
import EntitlementIcon from '../../../entitlement-icon/entitlement-icon';
import { FormattedDate } from '../../../formatted';
import { ColGroup, SearchResultsTable, TableBody, TableHead } from '../../../search-results-table';
import SimpleTD from '../../../simple-td/src/simple-td';
import { fetchProductsForInvoiceGroup } from './queries/fetch-products-for-invoice-group';
import { useCallback } from 'react';
import Tooltip from '@atlaskit/tooltip';

export const PAGE_TAB_SEARCH_RESULTS_LOAD_MORE = 15;

interface ProductTabProps {
  invoiceGroupId?: string;
  label?: string;
}

const columns = [
  {
    name: 'Product',
    customClassNames: 'col-20',
  },
  {
    name: 'ID',
    customClassNames: 'col-20',
  },
  {
    name: 'Billing frequency',
    customClassNames: 'col-20',
  },
  {
    name: 'Next bill',
    customClassNames: 'col-20',
  },
  {
    name: 'Last billed',
    customClassNames: 'col-20',
  },
  {
    name: '',
  },
];

const CloudProduct = ({ product }: { product: InvoiceGroupProduct }): JSX.Element => {
  return (
    <Link
      to={`${STATUSPAGE_PATH}/${encodeURIComponent(product?.id)}`}
      onClick={(e) => e.stopPropagation()}
      className="avatar-container"
    >
      {' '}
      {!!product?.productId && (
        <EntitlementIcon productId={product?.productId} isCloud={true} className="avatar medium" />
      )}
      {product?.productName ?? 'Unnamed product'}
    </Link>
  );
};

const ProductTab = ({ invoiceGroupId, label }: ProductTabProps) => {
  const { data, loading, error, fetchMore } = useQuery<
    FetchProductsForInvoiceGroupQuery,
    FetchProductsForInvoiceGroupQueryVariables
  >(fetchProductsForInvoiceGroup, {
    variables: {
      id: invoiceGroupId ?? '',
    },
    skip: !invoiceGroupId,
  });

  const totalCount = data?.invoiceGroup?.products?.totalCount ?? 0;

  const hasNoResults = !loading && (!data || data?.invoiceGroup?.products?.totalCount === 0);
  const products = data?.invoiceGroup?.products?.nodes;
  const resultsCount = products?.length ?? 0;
  const cursor = data?.invoiceGroup?.products?.pageInfo?.endCursor;

  const handleOnLoadMoreClick = useCallback(() => {
    return fetchMore({
      variables: {
        searchOptions: {
          cursor: cursor,
          first: PAGE_TAB_SEARCH_RESULTS_LOAD_MORE,
        },
      },
      //@ts-ignore
      updateQuery: (previousResult, { fetchMoreResult }: { fetchMoreResult: FetchProductsForInvoiceGroupQuery }) => {
        if (fetchMoreResult?.invoiceGroup?.products?.nodes && previousResult?.invoiceGroup?.products?.nodes) {
          fetchMoreResult.invoiceGroup.products.nodes = [
            ...previousResult.invoiceGroup.products.nodes,
            ...fetchMoreResult.invoiceGroup.products?.nodes,
          ];
        }
        return fetchMoreResult;
      },
    });
  }, [fetchMore]);

  const optionalTooltip = (billingStatus: string | undefined | null) => {
    const optionalTooltips = [
      {
        predicate: billingStatus === 'SUSPENDED',
        description: 'Deactivated',
      },
      {
        predicate: billingStatus === 'INACTIVE',
        description: 'Deleted',
      },
    ];

    return optionalTooltips.find((tooltip) => tooltip.predicate === true);
  };

  return (
    <SearchResultsTable
      id="invoice-groups-products"
      isLoading={loading}
      error={error?.toString()}
      noResults={hasNoResults}
      heading={label}
      loadMore={{
        showLoadMore: totalCount !== 0 && resultsCount < totalCount,
        totalCount,
        resultsCount,
        onLoadMoreClick: handleOnLoadMoreClick,
      }}
      entityName="invoice groups product"
    >
      <ColGroup columns={columns} />
      <TableHead columns={columns} />
      <TableBody>
        {(products ?? []).map(
          //@ts-ignore
          (product: InvoiceGroupProduct, index: number) => {
            const canShowToolTip = optionalTooltip(product?.billingStatus);
            return (
              <tr
                className={classNames('search-result', {
                  deactivated: product?.billingStatus === 'SUSPENDED',
                  deleted: product?.billingStatus === 'INACTIVE',
                })}
                key={`search-result-${index}`}
              >
                <td>
                  {product?.id ? (
                    canShowToolTip !== undefined ? (
                      <Tooltip position="bottom" content={canShowToolTip.description}>
                        <CloudProduct product={product} />
                      </Tooltip>
                    ) : (
                      <CloudProduct product={product} />
                    )
                  ) : (
                    'Not available'
                  )}
                </td>
                {product?.entitlementNumber ? <SimpleTD text={product?.entitlementNumber} /> : 'n/a'}
                <td>{product?.billingFrequency ? capitalize(product?.billingFrequency) : 'n/a'}</td>
                {product?.latestPeriodEndDate ? (
                  <td>
                    <FormattedDate date={product?.latestPeriodEndDate} />
                  </td>
                ) : (
                  'n/a'
                )}
                {product?.latestPeriodStartDate ? (
                  <td>
                    <FormattedDate date={product?.latestPeriodStartDate} />
                  </td>
                ) : (
                  'n/a'
                )}

                <td />
              </tr>
            );
          }
        )}
      </TableBody>
    </SearchResultsTable>
  );
};

export default ProductTab;
